import { useState } from "react";
import { api } from "@/lib/api";
import { Bell, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Small "Notify me" button + inline capture panel for the boutique waitlist.
 * Sits on top of each product tile. Uses stopPropagation so it doesn't fire
 * the tile's Link click.
 */
export default function WaitlistButton({ product }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/waitlist", { email: email.trim(), product_slug: product.slug, product_name: product.name });
      setDone(true);
      toast.success("You're on the boutique waitlist");
    } catch {
      toast.error("Could not join waitlist. Please try again.");
    }
    setBusy(false);
  };

  return (
    <div className="absolute top-3 right-3 z-20" onClick={(e) => e.stopPropagation()}>
      {!open ? (
        <button
          data-testid={`waitlist-open-${product.slug}`}
          onClick={(e) => { e.preventDefault(); setOpen(true); }}
          className="w-9 h-9 flex items-center justify-center bg-ivory/90 backdrop-blur border border-black/10 hover:border-goldDark hover:text-goldDark transition-colors duration-300"
          title="Notify me when this returns to boutique"
        >
          <Bell className="w-4 h-4" strokeWidth={1.25} />
        </button>
      ) : (
        <div className="bg-ivory border border-goldDark/40 shadow-lg p-4 w-72" data-testid={`waitlist-panel-${product.slug}`}>
          <div className="flex items-start justify-between mb-3">
            <p className="text-[10px] tracking-widest-plus uppercase text-goldDark">Boutique waitlist</p>
            <button onClick={(e) => { e.preventDefault(); setOpen(false); setDone(false); }}>
              <X className="w-4 h-4 text-ink/50" strokeWidth={1.25} />
            </button>
          </div>
          {done ? (
            <p className="font-editorial text-sm text-ink" data-testid={`waitlist-done-${product.slug}`}>
              We will write to you the moment {product.name} returns.
            </p>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <p className="text-xs text-ink/70 leading-snug">
                Notify me when <span className="font-editorial">{product.name}</span> returns to boutique.
              </p>
              <input
                data-testid={`waitlist-email-${product.slug}`}
                type="email" required autoFocus
                value={email} onChange={(e) => setEmail(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                placeholder="your@email.com"
                className="w-full border-b border-ink/25 focus:border-goldDark bg-transparent py-2 text-sm outline-none"
              />
              <button
                data-testid={`waitlist-submit-${product.slug}`}
                disabled={busy}
                className="w-full bg-ink text-ivory py-2 text-[10px] tracking-widest-plus uppercase hover:bg-goldDark transition-colors duration-500 disabled:opacity-40"
              >
                {busy ? "Adding…" : "Notify me"}
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
